/**
 * Device check, phase 5 (11/2/2026): "Match from photos".
 *
 * The office half of "Can't read the tag?". Each row is a device a tech
 * couldn't check against a readable tag: the 3 photos (whole device, body
 * casting, check covers) or a tag photo whose model isn't on the state list.
 * The office looks, taps the model, and marks it "Checked by office from
 * photos" — who and when are recorded, and the photo stays the proof.
 * Devices on a live PO or an open quote come first (that's where a wrong
 * answer costs a trip). Renders only while something is waiting. No alerts:
 * this card is the one home for it.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ModelTapList } from "@/components/model-tap-list";

export interface PhotoMatchRow {
    id: string;
    name: string | null;
    manufacturer: string | null;
    model: string | null;
    sizeLabel: string | null;
    assemblyType: string | null;
    serialNumber: string | null;
    siteId: string;
    siteName: string | null;
    customerName: string | null;
    why: string;
    onOpenPo: boolean;
    onOpenQuote: boolean;
    photos: Array<{ id: string; kind: string; url: string }>;
}

const TYPES: Array<{ value: string; label: string }> = [
    { value: "dc", label: "DC" },
    { value: "dcda", label: "DC detector" },
    { value: "rpz", label: "RPZ" },
    { value: "rpda", label: "RP detector" },
    { value: "pvb", label: "PVB" },
    { value: "svb", label: "SVB" },
    { value: "dcda_ii", label: "DCDA II" },
    { value: "rpda_ii", label: "RPDA II" },
];
const PHOTO_WORDS: Record<string, string> = {
    nameplate: "Tag",
    whole_device: "Whole device",
    body_casting: "Body casting",
    check_covers: "Check covers",
    lay_length: "Lay length",
    bypass_tag: "Bypass tag",
};

export function PhotoMatchCard() {
    const q = useQuery({
        queryKey: ["device-check", "photo-match"],
        queryFn: () => api.get<{ count: number; rows: PhotoMatchRow[] }>("/api/device-check/photo-match?limit=100"),
        refetchInterval: 120_000,
    });
    const [open, setOpen] = useState(false);
    const rows = q.data?.rows ?? [];
    if (rows.length === 0) return null;
    return (
        <div
            className="border-b border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20"
            data-testid="photo-match-card"
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-semibold text-amber-900 dark:text-amber-100"
            >
                <Camera className="h-4 w-4" />
                Match from photos: {rows.length} device{rows.length === 1 ? "" : "s"} the tech couldn&apos;t read
                {rows.some((r) => r.onOpenPo) && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-900">
                        {rows.filter((r) => r.onOpenPo).length} on a parts order
                    </span>
                )}
                <span className="ml-auto">
                    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </span>
            </button>
            {open && (
                <div className="space-y-3 px-4 pb-4">
                    <p className="text-xs text-foreground/70">
                        Look at the photos and tap the model. The device is then checked by the office from photos, with
                        your name. Can&apos;t tell? Leave it — the next tech there tries the tag again.
                    </p>
                    {rows.map((r) => (
                        <PhotoMatchRowView key={r.id} row={r} />
                    ))}
                </div>
            )}
        </div>
    );
}

export function PhotoMatchRowView({ row }: { row: PhotoMatchRow }) {
    const qc = useQueryClient();
    const [manufacturer, setManufacturer] = useState(row.manufacturer ?? "");
    const [assemblyType, setAssemblyType] = useState((row.assemblyType ?? "").toLowerCase());
    const [sizeLabel, setSizeLabel] = useState(row.sizeLabel ?? "");
    const [model, setModel] = useState("");
    const save = useMutation({
        mutationFn: () =>
            api.post(`/api/assets/${row.id}/match-from-photos`, { manufacturer, model, sizeLabel, assemblyType }),
        onSuccess: () => {
            toast.success("Checked by office from photos.");
            qc.invalidateQueries({ queryKey: ["device-check"] });
        },
        onError: (e) =>
            toast.error(
                e instanceof ApiError && e.body && typeof e.body === "object" && "message" in e.body
                    ? String((e.body as { message: unknown }).message)
                    : "Couldn't save. Try again.",
            ),
    });
    const ready = !!manufacturer.trim() && !!model.trim() && !!sizeLabel.trim() && !!assemblyType;
    return (
        <div className="rounded-lg border bg-card p-3" data-testid="photo-match-row">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <Link to={`/assets/${row.id}`} className="font-semibold hover:underline">
                    {row.name ?? "Device"}
                </Link>
                <span className="text-foreground/60">
                    {row.customerName} · {row.siteName}
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                    {row.why}
                </span>
                {row.onOpenPo && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-900">
                        On a parts order
                    </span>
                )}
                {!row.onOpenPo && row.onOpenQuote && (
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-900">
                        On a quote
                    </span>
                )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                {row.photos.map((p) => (
                    <a
                        key={p.id}
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block w-32 overflow-hidden rounded-md border"
                    >
                        <img src={p.url} alt={PHOTO_WORDS[p.kind] ?? p.kind} className="h-24 w-full object-cover" />
                        <div className="px-1.5 py-1 text-[11px] font-semibold">{PHOTO_WORDS[p.kind] ?? p.kind}</div>
                    </a>
                ))}
            </div>
            <div className="mt-1 text-xs text-foreground/60">
                On file:{" "}
                {[row.sizeLabel, row.manufacturer, row.model, row.serialNumber && `SN ${row.serialNumber}`]
                    .filter(Boolean)
                    .join(" · ") || "—"}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-4">
                <input
                    aria-label="Make"
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    placeholder="Make"
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                />
                <select
                    aria-label="Type"
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    value={assemblyType}
                    onChange={(e) => setAssemblyType(e.target.value)}
                >
                    <option value="">Type…</option>
                    {TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                            {t.label}
                        </option>
                    ))}
                </select>
                <input
                    aria-label="Size"
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    placeholder='Size, e.g. 4"'
                    value={sizeLabel}
                    onChange={(e) => setSizeLabel(e.target.value)}
                />
                <ModelTapList
                    value={model}
                    onChange={setModel}
                    manufacturer={manufacturer}
                    assemblyType={assemblyType}
                    sizeLabel={sizeLabel}
                />
            </div>
            <div className="mt-2 flex justify-end">
                <Button
                    size="sm"
                    disabled={!ready || save.isPending}
                    onClick={() => save.mutate()}
                    data-testid="photo-match-save"
                >
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Mark checked from photos
                </Button>
            </div>
        </div>
    );
}
